var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { Icon } from '../Icon';
import { getContent } from '../../utilities/getContent';
const defaultProps = {
    el: 'button',
};
/**
 * **Buttons** provide a possible user actions
 */
export const Button = React.forwardRef((_a, ref) => {
    var { children, className, color = 'default', content, el: Element = defaultProps.el, fill = 'solid', full, href, icon, iconName, iconPosition = 'left', inactive, large, loading, negative, outline, primary, selected, size = 'medium', small, text, type, width = 'auto', xsmall, onFocus, onBlur } = _a, props = __rest(_a, ["children", "className", "color", "content", "el", "fill", "full", "href", "icon", "iconName", "iconPosition", "inactive", "large", "loading", "negative", "outline", "primary", "selected", "size", "small", "text", "type", "width", "xsmall", "onFocus", "onBlur"]);
    const getTagName = () => {
        // If `el` is passed in and isn't default, it is always used
        if (Element !== defaultProps.el)
            return Element;
        // If `href` is passed in, the element becomes an anchor
        if (href)
            return 'a';
        // Is default, use it
        return Element;
    };
    const getTabIndex = () => {
        if (props.tabIndex)
            return props.tabIndex;
        if (getTagName() !== 'button')
            0;
        return null;
    };
    const getColorClass = () => {
        let realColor = color;
        // Override string props with boolean values
        if (primary)
            realColor = 'primary';
        if (negative)
            realColor = 'negative';
        const ButtonColorClasses = {
            primary: 'Button--blue',
            blue: 'Button--blue',
            default: 'Button--grey',
            grey: 'Button--grey',
            negative: 'Button--red',
            red: 'Button--red',
        };
        return ButtonColorClasses[realColor];
    };
    const getFillClass = () => {
        let realFill = fill;
        if (outline)
            realFill = 'outline';
        if (text || fill === 'subtle' || fill === 'none')
            realFill = 'subtle';
        if (realFill === 'solid')
            return 'Button--solid';
        if (realFill === 'outline')
            return 'Button--outline';
        if (realFill === 'subtle')
            return 'Button--subtle';
    };
    const getSizeClass = () => {
        let realSize = size;
        if (xsmall)
            realSize = 'xsmall';
        if (small)
            realSize = 'small';
        if (large)
            realSize = 'large';
        if (realSize === 'xsmall')
            return 'Button--xsmall';
        if (realSize === 'small')
            return 'Button--small';
        if (realSize === 'large')
            return 'Button--large';
    };
    const getWidth = () => {
        if (full)
            return 'full';
        return width;
    };
    const getWidthValue = () => {
        const width = getWidth();
        return width !== 'auto' && width !== 'full' ? width : false;
    };
    const getWidthClass = () => {
        return getWidth() === 'full' ? 'Button--full' : null;
    };
    const getTypeAttribute = () => {
        if (type)
            return { type };
        if (getTagName() === 'button')
            return { type: 'button' };
    };
    React.useEffect(() => {
        if (text)
            console.warn("@servicetitan/design-system: The `text` prop of the Button component has been deprecated. Please use `fill='subtle'` instead.");
        if (fill === 'none')
            console.warn("@servicetitan/design-system: The `fill='none'` prop of the Button component has been deprecated. Please use `fill='subtle'` instead.");
        if (getTagName() !== 'button' && !!getTypeAttribute())
            throw new Error('@servicetitan/design-system: The Button component is using incompatible props. Only button elements can have type attributes.');
        if (getTagName() !== 'a' && href)
            throw new Error('@servicetitan/design-system: The Button component is using incompatible props. Only anchor elements can have href attributes.');
    });
    const hasContent = () => {
        if (children != null)
            return true;
        if (content != null)
            return true;
        return false;
    };
    const hasIcon = () => {
        if (iconName || icon)
            return true;
        return false;
    };
    const renderIcon = () => {
        if (!hasIcon())
            return;
        let ButtonIcon = null;
        if (iconName)
            ButtonIcon = (React.createElement(Icon, { className: "Button__icon-svg", name: iconName }));
        if (icon)
            ButtonIcon = icon;
        const ButtonIconClasses = classnames('Button__icon', {
            'Button__icon--left': hasContent() && iconPosition === 'left',
            'Button__icon--right': hasContent() && iconPosition === 'right',
        });
        return React.createElement("span", { className: ButtonIconClasses }, ButtonIcon);
    };
    const preventClicks = props.disabled || inactive || loading;
    const ButtonClasses = classnames('Button', className, getColorClass(), getFillClass(), getSizeClass(), getWidthClass(), {
        'Button--disabled': props.disabled,
        'Button--inactive': preventClicks,
        'Button--loading': loading,
        'Button--icon-only': !hasContent(),
        'Button--selected': selected,
    });
    const ButtonElement = getTagName();
    !hasContent() &&
        !props['aria-label'] &&
        console.warn(`Accessibility warning:
				\naria-label attribute does not exist or is empty for button that does not have inner text that is visible to screen readers`);
    return (React.createElement(ButtonElement, Object.assign({ className: ButtonClasses, disabled: preventClicks, href: href, role: getTagName() !== 'button' ? 'button' : null, style: { width: getWidthValue() }, tabIndex: getTabIndex(), ref: ref, "data-anvil-component": "Button" }, getTypeAttribute(), props),
        React.createElement("span", { className: "Button__content" },
            iconPosition === 'left' && renderIcon(),
            getContent({ children, content }),
            iconPosition === 'right' && renderIcon())));
});
//# sourceMappingURL=Button.js.map